import { environment } from "@environments/environment";

/**
 * Builder for the ADL deep link that opens a package inside the **myAIS app**.
 *
 * Marketing hands this URL out; before it existed here they assembled it by pasting
 * the offering and price ids into a template by hand, which is where the broken links
 * came from. Everything about the shape below is the app team's contract — treat it as
 * fixed, not as a URL to be "tidied up".
 *
 * ```
 * https://<host>/openWebToApp?URI=myais://ProductDetail?productOfferingCodePre=<CODE>&productOfferingCodePost=<CODE>&purchaseFrom=basePackage&callbackRoute=/product-package/usage-and-package-detail
 * ```
 *
 * The link is keyed on the catalog **Product code** alone
 * (`externalIdentifier[externalIdentifierType="ProductCode"].id`). Both halves carry the
 * same code: the catalog has one code per price, and the app picks the half matching the
 * subscriber's network type.
 *
 * Two parameters the portal used to send are deliberately absent.
 * `productOfferingId` carried the offering id, which is not what the app resolves by.
 * `productOfferingPriceIdPost` carried the POP id and has no `…Pre` counterpart —
 * sending only the Post half described a postpaid subscriber to a link meant to serve
 * both. The code pair replaces the two of them.
 *
 * Two things about it are easy to "fix" and must not be:
 *
 * 1. **The nested URI is not percent-encoded.** The `?` and `&` after
 *    `URI=myais://ProductDetail` belong to the deep link's own query string. Encoding
 *    them, or normalising that `?` into a separator, hands the app a URI it cannot
 *    route (`myais://ProductDetail&productOfferingId=…`). The banner feature's
 *    `normalizeQueryString` documents the same trap from the other direction.
 * 2. **Parameter order is part of the contract** as given, so it is written out
 *    literally rather than assembled from a map.
 */

/** ADL host for this environment — declared in the environment file (see
 *  `environment.adl.deepLinkHost`) rather than derived from the zone name, so
 *  the environment file swapped in at build time is the only thing that decides
 *  which app a link opens. Non-production files all point at the TEST host the
 *  app team publishes; environment.adl.spec guards that only prod maps to the
 *  production app. */
export const ADL_DEEP_LINK_DOMAIN = `${environment.adl.deepLinkHost}.adldigitalservice.com`;

/** Fixed for this link type — the app resolves the purchase context and the back
 *  destination from them, and both are the same for every package. */
const PURCHASE_FROM = "basePackage";
const CALLBACK_ROUTE = "/product-package/usage-and-package-detail";

/**
 * Build the app link for one price of one package.
 *
 * `productCode` is the catalog Product code of THAT price —
 * `externalIdentifier[externalIdentifierType="ProductCode"].id`, surfaced as the flat
 * `prices[].productCode` on both the list and the detail payloads. It is not the
 * offering id: the two look alike in a URL and only the app can tell you which one
 * failed to resolve.
 *
 * Returns `""` when there is no code, which is the caller's signal to render no control
 * at all — a half-built deep link looks valid and fails silently on a phone. A price the
 * catalog has not coded is the common case here, not an edge one.
 *
 * Only the code is escaped. Real codes (`O25022724`) pass through unchanged; the
 * escaping is there so a stray `&` can never invent a parameter. The rest of the URI
 * stays raw, per the contract above.
 */
export function buildProductDetailAppLink(productCode?: string | null): string {
  const code = `${productCode ?? ""}`.trim();
  if (!code) return "";

  const encodedCode = encodeURIComponent(code);

  return (
    `https://${ADL_DEEP_LINK_DOMAIN}/openWebToApp?URI=myais://ProductDetail` +
    `?productOfferingCodePre=${encodedCode}` +
    `&productOfferingCodePost=${encodedCode}` +
    `&purchaseFrom=${PURCHASE_FROM}` +
    `&callbackRoute=${CALLBACK_ROUTE}`
  );
}
